import { useLayoutEffect, useSyncExternalStore } from 'react';
import type { Dispatch } from '@reduxjs/toolkit';
import { setUserToken } from '../../../redux/slices/appInfo/slice';
import { useUserTokenDecoded } from '../../../redux/slices/appInfo/hooks';
import { store as persistentStore } from '../../../store/Store';
import { removeSignedInHint } from '../session/signedInHintStore';
import { pauseAnalytics } from '../../analytics/sml/analyticsPause';
import { loginAsAdminOf } from './loginAsClaim';

/** The admin email while the session is an admin session, else null. */
export function useLoginAsAdmin(): string | null {
  return loginAsAdminOf(useUserTokenDecoded());
}

/**
 * Stops the trackers while the session is an admin session. A layout effect, so it runs before
 * the effects of the screens (identify, screen and event calls) of the same render.
 */
export function useLoginAsAnalyticsPause(): void {
  const isAdminSession = useLoginAsAdmin() !== null;
  useLayoutEffect(() => {
    if (isAdminSession) {
      pauseAnalytics();
    }
  }, [isAdminSession]);
}

let ended = false;
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const readEnded = (): boolean => ended;

function setEnded(value: boolean): void {
  ended = value;
  listeners.forEach((listener) => listener());
}

/**
 * True after an admin session ended: its token expired, or the API refused it. `LoginAsGate` then
 * shows the message.
 */
export function useLoginAsEnded(): boolean {
  return useSyncExternalStore(subscribe, readEnded, readEnded);
}

/**
 * Ends an admin session whose token is dead (it expired, or the API answered 401 for it): the
 * token leaves the store, and the page shows "The admin session ended after one hour.". No server
 * call: the token is dead already.
 */
export function endExpiredLoginAsSession(dispatch: Dispatch): void {
  dispatch(setUserToken({ token: null }));
  persistentStore.remove('userToken').catch(console.error);
  removeSignedInHint();
  setEnded(true);
}

/** For tests only. */
export function resetLoginAsEndedForTests(): void {
  setEnded(false);
}
